import React, { useEffect, useState } from 'react';
import { Card, Button, Input, Modal, Form, message, Tag, Space, Upload } from 'antd';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
// import 'ag-grid-community/styles/ag-grid.css';
// import 'ag-grid-community/styles/ag-theme-alpine.css';
// import '@/ag-grid-overrides.css';
import api, { toggleWriterStatus } from '@/services/api';
import { useTheme } from '@/contexts/ThemeContext';

const Writers = () => {
  const { isDark } = useTheme();
  const [writers, setWriters] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingWriter, setEditingWriter] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchWriters();
  }, []);

  const fetchWriters = async () => {
    try {
      const response = await api.get('/writers');
      setWriters(response.data.data);
    } catch (error) {
      message.error('Failed to fetch writers');
    }
  };

  const handleToggleStatus = async (writer) => {
    try {
      const newStatus = writer.status === 'active' ? 'disabled' : 'active';
      await toggleWriterStatus(writer.uid);
      message.success(`Writer ${newStatus === 'active' ? 'enabled' : 'disabled'} successfully`);
      fetchWriters();
    } catch (error) {
      message.error('Failed to update writer status');
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingWriter) {
        await api.put(`/writers/${editingWriter.id}`, values);
        message.success('Writer updated successfully');
      } else {
        await api.post('/writers', { ...values, status: 'active' });
        message.success('Writer created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingWriter(null);
      fetchWriters();
    } catch (error) {
      message.error('Failed to save writer');
    }
  };

  const openEditModal = (writer) => {
    setEditingWriter(writer);
    form.setFieldsValue(writer);
    setModalVisible(true);
  };

  const openCreateModal = () => {
    setEditingWriter(null);
    form.resetFields();
    setModalVisible(true);
  };

  const columnDefs = [
    { field: 'id', headerName: 'ID', width: 70, filter: true },
    {
      field: 'avatar',
      headerName: 'Avatar',
      width: 100,
      cellRenderer: (params) => (
        <img
          src={params.value}
          alt="avatar"
          style={{ width: 40, height: 40, objectFit: 'cover' }}
        />
      ),
    },
    { field: 'name', headerName: 'Name', flex: 1, filter: true },
    { field: 'email', headerName: 'Email', flex: 1, filter: true },
    { field: 'articleCount', headerName: 'Articles', width: 100, cellStyle: { fontFamily: 'JetBrains Mono, monospace' } },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      cellRenderer: (params) => (
        <Tag
          color={params.value === 'active' ? 'success' : 'default'}
          style={{ borderRadius: 0, fontWeight: 500, fontSize: 11, textTransform: 'uppercase' }}
        >
          {params.value}
        </Tag>
      ),
    },
    { field: 'joinedDate', headerName: 'Joined', width: 130 },
    {
      headerName: 'Actions',
      width: 200,
      cellRenderer: (params) => (
        <Space>
          <Button
            size="small"
            style={{ borderRadius: 0, fontWeight: 500 }}
            onClick={() => openEditModal(params.data)}
            data-testid={`edit-writer-${params.data.id}`}
          >
            Edit
          </Button>
          <Button
            size="small"
            danger={params.data.status === 'active'}
            style={{ borderRadius: 0, fontWeight: 500 }}
            onClick={() => handleToggleStatus(params.data)}
            data-testid={`toggle-writer-${params.data.id}`}
          >
            {params.data.status === 'active' ? 'Disable' : 'Enable'}
          </Button>
        </Space>
      ),
    },
  ];

  const filteredWriters = writers.filter((writer) =>
    Object.values(writer).some((val) =>
      String(val).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, fontFamily: 'IBM Plex Sans, sans-serif', letterSpacing: '-0.02em', marginBottom: 8 }}>
            Writers Management
          </h1>
          <p style={{ fontSize: 14, color: isDark ? '#A1A1AA' : '#71717A', margin: 0 }}>
            Manage all writers and contributors
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openCreateModal}
          style={{ borderRadius: 0, fontWeight: 500, height: 40 }}
          data-testid="create-writer-button"
        >
          Add Writer
        </Button>
      </div>

      <Card
        style={{
          borderRadius: 0,
          border: `1px solid ${isDark ? '#27272A' : '#E4E4E7'}`,
          background: isDark ? '#18181B' : '#FFFFFF',
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search writers..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 400, borderRadius: 0 }}
            data-testid="writers-search-input"
          />
        </div>
        <div className={isDark ? 'ag-theme-alpine dark' : 'ag-theme-alpine'} style={{ height: 600, width: '100%' }}>
          <AgGridReact
            rowData={filteredWriters}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={20}
            domLayout="normal"
          />
        </div>
      </Card>

      <Modal
        title={editingWriter ? 'Edit Writer' : 'Create Writer'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingWriter(null);
        }}
        footer={null}
        styles={{ borderRadius: 0 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: 24 }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter writer name' }]}
          >
            <Input style={{ borderRadius: 0 }} data-testid="writer-name-input" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email', message: 'Please enter valid email' }]}
          >
            <Input style={{ borderRadius: 0 }} data-testid="writer-email-input" />
          </Form.Item>
          <Form.Item
            name="bio"
            label="Bio"
          >
            <Input.TextArea rows={3} style={{ borderRadius: 0 }} data-testid="writer-bio-input" />
          </Form.Item>
          <Form.Item
            name="avatar"
            label="Avatar URL"
          >
            <Input style={{ borderRadius: 0 }} placeholder="https://example.com/avatar.jpg" data-testid="writer-avatar-input" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ borderRadius: 0 }} data-testid="writer-submit-button">
                {editingWriter ? 'Update' : 'Create'}
              </Button>
              <Button onClick={() => setModalVisible(false)} style={{ borderRadius: 0 }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Writers;
